/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Grid2 as Grid } from "@mui/material";
import dayjs from "dayjs";
import { FC } from "react";
import { IconComponent } from "src/shared/components";
import { ECalendarRange } from "src/shared/components/fields/DateRangeField/model/types";
import { calendarFilterParamsFormat } from "../../model/constants";
import {
  EFilterType,
  EFilterCalendarType,
  IFilterItem,
  IFilterModel,
  IFilterValue,
  ICalendarFilterInfo,
  TFilterPossibleValues,
  IFilterMonthItem,
  IFilterCalendarItem,
} from "../../model/types";
import TableFilterAutocomplete from "./TableFilterAutocomplete";
import TableFilterCalendar from "./TableFilterCalendar";
import TableFilterCheckbox from "./TableFilterCheckbox";
import TableFilterMonth from "./TableFilterMonth";
import TableFilter from "./TableFilterSelect";

interface ITableFiltersProps {
  tableFilterModel: IFilterModel;
  after?: React.ReactNode;
}

const TableFilters: FC<ITableFiltersProps> = ({ tableFilterModel, after }) => {
  const { filters, onFilter, onReset } = tableFilterModel;

  const setFilters = (v: IFilterValue<TFilterPossibleValues>) => {
    const next: IFilterValue<TFilterPossibleValues>[] = [];

    filters.forEach((filter) => {
      if (filter.type === EFilterCalendarType.CALENDAR) return;
      if (filter.type === EFilterCalendarType.MONTH) return;

      const regular = filter as IFilterItem<TFilterPossibleValues>;

      if (regular.name === v.name) {
        regular.onChange?.(String(v.value ?? ""));
      }

      next.push({
        name: regular.name,
        value: regular.name === v.name ? v.value : regular.value,
        itemColor: regular.itemColor,
      });
    });

    if (v.name === "partner_name") {
      next.push({ name: "partner_id", value: null });
    }

    onFilter(next);
  };

  const setMonthFilter = (v: IFilterValue<string | null>) => {
    onFilter([{ name: v.name, value: v.value }]);
  };

  const setCalendarFilters = (v: IFilterValue<ICalendarFilterInfo>) => {
    const calendarItemFilter = filters.find((filter) => filter.name === v.name);
    const filtersValue: IFilterValue<string | ECalendarRange | null>[] = [];
    if (
      calendarItemFilter &&
      calendarItemFilter.type === EFilterCalendarType.CALENDAR
    ) {
      const { startDate, endDate, calendarRange } = v.value;

      filtersValue.push({
        name: `${calendarItemFilter.name}From`,
        value: startDate
          ? dayjs(startDate.toISOString())
              .startOf("D")
              .format(calendarFilterParamsFormat)
          : null,
      });
      filtersValue.push({
        name: `${calendarItemFilter.name}To`,
        value: endDate
          ? dayjs(endDate.toISOString())
              .endOf("D")
              .format(calendarFilterParamsFormat)
          : null,
      });
      filtersValue.push({
        name: `${calendarItemFilter.name}CalendarRange`,
        value: calendarRange,
      });
    }

    onFilter(filtersValue);
  };

  let isFilterFilled = false;

  if (onReset) {
    isFilterFilled = filters.some((item) => {
      if (item.hideClearButton) {
        return false;
      }
      if (
        item.type === EFilterType.SINGLE_SELECT ||
        item.type === EFilterType.SINGLE_AUTOCOMPLETE
      ) {
        return item.value !== "" && item.value !== null;
      }
      if (
        (item.type === EFilterType.MULTI_SELECT ||
          item.type === EFilterType.MULTI_AUTOCOMPLETE) &&
        Array.isArray(item.value)
      ) {
        return item.value.length > 0;
      }
      if (item.type === EFilterType.BOOLEAN) {
        return item.value !== false;
      }
      if (item.type === EFilterCalendarType.CALENDAR) {
        return item.value.startDate && item.value.endDate;
      }
      if (item.type === EFilterCalendarType.MONTH) {
        const monthItem = item as IFilterMonthItem;

        return monthItem.value !== null && monthItem.value !== "";
      }
    });
  }

  return (
    <Box>
      <Grid
        spacing={2}
        container
        sx={{
          flexWrap: "wrap",
        }}
      >
        <Grid
          container
          sx={{
            flexWrap: "wrap",
          }}
        >
          <Grid
            spacing={3}
            container
            sx={{
              flexWrap: "wrap",
            }}
          >
            {filters.map((filter) => {
              if (filter.type === EFilterCalendarType.CALENDAR) {
                const cal = filter as IFilterCalendarItem;

                return (
                  <Grid key={cal.name}>
                    <TableFilterCalendar
                      value={cal.value}
                      name={cal.name}
                      onChange={setCalendarFilters}
                      title={cal.title}
                      minDate={cal.minDate}
                      maxDate={cal.maxDate}
                    />
                  </Grid>
                );
              }

              if (filter.type === EFilterCalendarType.MONTH) {
                const m = filter as IFilterMonthItem;

                return (
                  <Grid key={m.name}>
                    <TableFilterMonth
                      name={m.name}
                      title={m.title}
                      value={m.value}
                      onChange={setMonthFilter}
                    />
                  </Grid>
                );
              }
              switch (filter.type) {
                case EFilterType.SINGLE_SELECT: {
                  return (
                    <Grid key={filter.name}>
                      <TableFilter
                        key={filter.name}
                        filter={filter as IFilterItem<string>}
                        onChange={setFilters}
                      />
                    </Grid>
                  );
                }
                case EFilterType.MULTI_SELECT: {
                  return (
                    <Grid key={filter.name}>
                      <TableFilter
                        multiple
                        key={filter.name}
                        filter={filter as IFilterItem<string[]>}
                        onChange={setFilters}
                      />
                    </Grid>
                  );
                }

                case EFilterType.MULTI_AUTOCOMPLETE: {
                  return (
                    <Grid key={filter.name}>
                      <TableFilterAutocomplete<string | number>
                        multiple
                        key={filter.name}
                        filter={filter as any}
                        onChange={setFilters}
                      />
                    </Grid>
                  );
                }

                case EFilterType.SINGLE_AUTOCOMPLETE: {
                  return (
                    <Grid key={filter.name}>
                      <TableFilterAutocomplete<string | number>
                        multiple={false}
                        key={filter.name}
                        filter={filter as any}
                        onChange={setFilters}
                      />
                    </Grid>
                  );
                }

                case EFilterType.BOOLEAN: {
                  return (
                    <Grid key={filter.name}>
                      <TableFilterCheckbox
                        key={filter.name}
                        filter={filter as unknown as IFilterItem<boolean>}
                        onChange={setFilters}
                      />
                    </Grid>
                  );
                }
              }
            })}
            {isFilterFilled && onReset && (
              <Grid>
                <Button
                  onClick={onReset}
                  sx={{
                    padding: (theme) => theme.spacing(2, 3),
                  }}
                  startIcon={
                    <IconComponent
                      sx={{ fontSize: "20px!important" }}
                      iconname="mop"
                    />
                  }
                >
                  Очистить
                </Button>
              </Grid>
            )}
            {after ? <Grid>{after}</Grid> : null}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TableFilters;
